import Phaser from 'phaser';
import { GAME_FONT_FAMILY } from '@shared/constants';
import { getItemTemplate } from '@shared/data/items';
import { describeModifiers } from '@shared/data/modifierDisplay';
import { getSkillDef } from '@shared/data/skills';
import { getRecipesForStation, type CraftingRecipe } from '@shared/data/recipes';
import { getCraftingStationMeta } from '@shared/data/craftingStations';
import { createCloseButton } from '../../sprites/IconAtlases';

/** Callbacks de la ventana de crafteo. */
export interface CraftingPanelCallbacks {
  /** Craftear una receta en la estación abierta. */
  onCraft: (stationId: string, recipeId: string) => void;
  /** Cantidad total de un item en el inventario del player. */
  getItemCount: (itemId: string) => number;
  /** Nivel actual del player en una skill. */
  getSkillLevel: (skillId: string) => number;
}

type RecipeState = 'locked' | 'missing' | 'ready';

const PANEL_W = 480;
const PANEL_H = 384;
const ROW_H = 32;
const LIST_X = 14; // offset dentro del panel
const LIST_W = 210;
const DETAIL_X = LIST_X + LIST_W + 12;

const COLOR_READY = '#e8e2d0';
const COLOR_LOCKED = '#6f6a5e';
const COLOR_MISSING = '#c9695e';
const COLOR_HAVE = '#8fce9a';

function font<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return { fontFamily: GAME_FONT_FAMILY, resolution: 2, ...style } as T;
}

/**
 * Ventana de CRAFTEO de una estación (banco de carpintería, futuro horno/fragua). Muestra la
 * lista de recetas del `stationType` con estado por color:
 *  - gris (`locked`)   = nivel de skill insuficiente (clickeable: se ven los detalles).
 *  - rojo (`missing`)  = tenés el nivel pero te faltan materiales.
 *  - normal (`ready`)  = crafteable ahora.
 * Panel de detalle: ícono grande, nombre, características (modifiers), materiales tenés/necesitás,
 * nivel/XP y botón Craftear. Phaser, filtrado a la uiCamera. Ver docs/CRAFTING_SYSTEM.md.
 */
export class CraftingPanel {
  private readonly scene: Phaser.Scene;
  private readonly cb: CraftingPanelCallbacks;

  private isOpen = false;
  private px = 0;
  private py = 0;

  private stationId: string | null = null;
  private recipes: CraftingRecipe[] = [];
  private selectedId: string | null = null;

  /** Todos los GameObjects (para ignore de la cámara main). */
  private parts: Phaser.GameObjects.GameObject[] = [];

  // Chrome
  private bg!: Phaser.GameObjects.Rectangle;
  private title!: Phaser.GameObjects.Text;
  private closeBtn: Phaser.GameObjects.Sprite | null = null;

  // Filas (se reconstruyen al abrir).
  private rows: Array<{ bg: Phaser.GameObjects.Rectangle; icon: Phaser.GameObjects.Sprite; name: Phaser.GameObjects.Text; recipeId: string }> = [];

  // Detalle (se crean una vez, se actualizan al seleccionar).
  private detailIcon!: Phaser.GameObjects.Sprite;
  private detailName!: Phaser.GameObjects.Text;
  private detailBody!: Phaser.GameObjects.Text;
  private craftBtnBg!: Phaser.GameObjects.Rectangle;
  private craftBtnLabel!: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene, cb: CraftingPanelCallbacks) {
    this.scene = scene;
    this.cb = cb;
    this.px = Math.floor((scene.scale.width - PANEL_W) / 2);
    this.py = Math.floor((scene.scale.height - PANEL_H) / 2);
    this.build();
    this.setVisible(false);
    this.applyCameraFilter();
  }

  private track<T extends Phaser.GameObjects.GameObject>(obj: T): T {
    this.parts.push(obj);
    return obj;
  }

  private build(): void {
    const { px, py } = this;
    this.bg = this.track(
      this.scene.add
        .rectangle(px, py, PANEL_W, PANEL_H, 0x1c1a17, 0.97)
        .setOrigin(0, 0)
        .setStrokeStyle(2, 0x6b5a3e)
        .setDepth(1200)
        .setScrollFactor(0)
        .setInteractive() // captura clicks para no atravesar al mundo
    );
    this.title = this.track(
      this.scene.add
        .text(px + PANEL_W / 2, py + 14, 'Crafteo', font({ fontSize: '18px', color: '#e8c98a' }))
        .setOrigin(0.5, 0.5)
        .setDepth(1201)
        .setScrollFactor(0)
    );
    this.closeBtn = createCloseButton(this.scene, px + PANEL_W - 16, py + 14, 18);
    if (this.closeBtn) {
      this.closeBtn.setDepth(1201).setScrollFactor(0);
      this.closeBtn.on('pointerdown', () => this.close());
      this.track(this.closeBtn);
    }

    // Detalle: ícono grande + nombre + cuerpo + botón craftear.
    const dx = px + DETAIL_X;
    this.detailIcon = this.track(
      this.scene.add.sprite(dx + 24, py + 60, 'item_icon_noicon').setDisplaySize(48, 48).setDepth(1201).setScrollFactor(0)
    );
    this.detailName = this.track(
      this.scene.add
        .text(dx + 56, py + 44, '', font({ fontSize: '15px', color: '#ffffff', wordWrap: { width: PANEL_W - DETAIL_X - 60 } }))
        .setDepth(1201)
        .setScrollFactor(0)
    );
    this.detailBody = this.track(
      this.scene.add
        .text(dx, py + 96, '', font({ fontSize: '12px', color: '#cfc8b8', lineSpacing: 3, wordWrap: { width: PANEL_W - DETAIL_X - 24 } }))
        .setDepth(1201)
        .setScrollFactor(0)
    );

    const btnY = py + PANEL_H - 34;
    this.craftBtnBg = this.track(
      this.scene.add
        .rectangle(dx, btnY, PANEL_W - DETAIL_X - 24, 26, 0x3a6b3f, 1)
        .setOrigin(0, 0)
        .setStrokeStyle(1, 0x2a4a2d)
        .setDepth(1201)
        .setScrollFactor(0)
        .setInteractive({ useHandCursor: true })
    );
    this.craftBtnBg.on('pointerdown', () => this.doCraft());
    this.craftBtnLabel = this.track(
      this.scene.add
        .text(dx + (PANEL_W - DETAIL_X - 24) / 2, btnY + 13, 'Craftear', font({ fontSize: '14px', color: '#ffffff' }))
        .setOrigin(0.5, 0.5)
        .setDepth(1202)
        .setScrollFactor(0)
    );
  }

  private applyCameraFilter(): void {
    const main = this.scene.cameras.main;
    if (main) main.ignore(this.parts);
  }

  private iconKey(itemId: string): string {
    const tpl = getItemTemplate(itemId);
    const key = tpl ? `item_icon_${tpl.icon}` : 'item_icon_noicon';
    return this.scene.textures.exists(key) ? key : 'item_icon_noicon';
  }

  // === API pública ===========================================================

  public isOpenPanel(): boolean {
    return this.isOpen;
  }

  /** Abre la ventana para una estación (id + tipo). */
  public open(stationId: string, stationType: string): void {
    this.stationId = stationId;
    this.recipes = getRecipesForStation(stationType);
    const meta = getCraftingStationMeta(stationType);
    this.title.setText(meta?.name ?? 'Crafteo');
    this.selectedId = this.recipes[0]?.id ?? null;
    this.rebuildRows();
    this.renderDetail();
    this.setVisible(true);
    this.isOpen = true;
  }

  public close(): void {
    this.isOpen = false;
    this.setVisible(false);
  }

  public toggle(stationId: string, stationType: string): void {
    if (this.isOpen && this.stationId === stationId) this.close();
    else this.open(stationId, stationType);
  }

  /** Refresca estados (colores + botón) cuando cambia el inventario/skills con el panel abierto. */
  public refresh(): void {
    if (!this.isOpen) return;
    this.paintRowStates();
    this.renderDetail();
  }

  private setVisible(v: boolean): void {
    for (const p of this.parts) (p as unknown as { setVisible?: (b: boolean) => void }).setVisible?.(v);
    for (const r of this.rows) {
      r.bg.setVisible(v);
      r.icon.setVisible(v);
      r.name.setVisible(v);
    }
  }

  // === Filas =================================================================

  private clearRows(): void {
    for (const r of this.rows) {
      r.bg.destroy();
      r.icon.destroy();
      r.name.destroy();
    }
    this.rows = [];
  }

  private rebuildRows(): void {
    this.clearRows();
    const main = this.scene.cameras.main;
    const x = this.px + LIST_X;
    let y = this.py + 42;
    for (const recipe of this.recipes) {
      const tpl = getItemTemplate(recipe.outputItemId);
      const rowBg = this.scene.add
        .rectangle(x, y, LIST_W, ROW_H - 3, 0x24211c, 1)
        .setOrigin(0, 0)
        .setDepth(1201)
        .setScrollFactor(0)
        .setInteractive({ useHandCursor: true });
      const icon = this.scene.add
        .sprite(x + 16, y + (ROW_H - 3) / 2, this.iconKey(recipe.outputItemId))
        .setDisplaySize(24, 24)
        .setDepth(1202)
        .setScrollFactor(0);
      const name = this.scene.add
        .text(x + 34, y + (ROW_H - 3) / 2, tpl?.name ?? recipe.outputItemId, font({ fontSize: '13px', color: COLOR_READY }))
        .setOrigin(0, 0.5)
        .setDepth(1202)
        .setScrollFactor(0);
      const rid = recipe.id;
      rowBg.on('pointerdown', () => {
        this.selectedId = rid;
        this.paintRowStates();
        this.renderDetail();
      });
      if (main) main.ignore([rowBg, icon, name]);
      this.rows.push({ bg: rowBg, icon, name, recipeId: rid });
      y += ROW_H;
    }
    this.paintRowStates();
  }

  /** Estado de una receta según nivel de skill + materiales. */
  private stateOf(recipe: CraftingRecipe): RecipeState {
    if (this.cb.getSkillLevel(recipe.skill) < recipe.requiredLevel) return 'locked';
    for (const inp of recipe.inputs) {
      if (this.cb.getItemCount(inp.itemId) < inp.qty) return 'missing';
    }
    return 'ready';
  }

  private paintRowStates(): void {
    for (const r of this.rows) {
      const recipe = this.recipes.find((x) => x.id === r.recipeId);
      if (!recipe) continue;
      const st = this.stateOf(recipe);
      const color = st === 'locked' ? COLOR_LOCKED : st === 'missing' ? COLOR_MISSING : COLOR_READY;
      r.name.setColor(color);
      r.icon.setAlpha(st === 'locked' ? 0.45 : 1);
      const selected = r.recipeId === this.selectedId;
      r.bg.setFillStyle(selected ? 0x3a3428 : 0x24211c, 1);
      r.bg.setStrokeStyle(selected ? 1 : 0, 0x6b5a3e);
    }
  }

  // === Detalle ===============================================================

  private renderDetail(): void {
    const recipe = this.recipes.find((x) => x.id === this.selectedId) ?? null;
    if (!recipe) {
      this.detailName.setText('');
      this.detailBody.setText('');
      this.detailIcon.setTexture('item_icon_noicon');
      this.setCraftEnabled(false);
      return;
    }
    const tpl = getItemTemplate(recipe.outputItemId);
    this.detailIcon.setTexture(this.iconKey(recipe.outputItemId)).setDisplaySize(48, 48);
    this.detailName.setText(`${tpl?.name ?? recipe.outputItemId}${recipe.outputQty > 1 ? ` ×${recipe.outputQty}` : ''}`);

    const lines: string[] = [];
    if (tpl?.description) lines.push(tpl.description, '');

    // Características (modifiers del output), si tiene.
    const mods = describeModifiers(tpl?.modifiers);
    if (mods.length > 0) {
      for (const m of mods) lines.push(`• ${m.label}`);
      lines.push('');
    }

    // Gate de nivel.
    const skillName = getSkillDef(recipe.skill)?.name ?? recipe.skill;
    const lvl = this.cb.getSkillLevel(recipe.skill);
    const levelOk = lvl >= recipe.requiredLevel;
    lines.push(`${levelOk ? '✓' : '✗'} ${skillName} ${lvl}/${recipe.requiredLevel}`);

    // Materiales.
    lines.push('Materiales:');
    let materialsOk = true;
    for (const inp of recipe.inputs) {
      const have = this.cb.getItemCount(inp.itemId);
      const enough = have >= inp.qty;
      if (!enough) materialsOk = false;
      const matName = getItemTemplate(inp.itemId)?.name ?? inp.itemId;
      lines.push(`  ${enough ? '✓' : '✗'} ${matName}  ${have}/${inp.qty}`);
    }
    lines.push('', `XP de ${skillName}: +${recipe.xp}`);

    this.detailBody.setText(lines.join('\n'));
    // Colorear el cuerpo de forma global según el bloqueo dominante.
    this.detailBody.setColor(!levelOk ? COLOR_LOCKED : !materialsOk ? COLOR_MISSING : COLOR_HAVE);

    this.setCraftEnabled(levelOk && materialsOk);
  }

  private setCraftEnabled(enabled: boolean): void {
    this.craftBtnBg.setFillStyle(enabled ? 0x3a6b3f : 0x2e2b26, 1);
    this.craftBtnBg.setAlpha(enabled ? 1 : 0.7);
    this.craftBtnLabel.setColor(enabled ? '#ffffff' : '#8a8578');
    (this.craftBtnBg as unknown as { input: { enabled: boolean } | null }).input &&
      (this.craftBtnBg.input!.enabled = enabled);
  }

  private doCraft(): void {
    if (!this.stationId || !this.selectedId) return;
    const recipe = this.recipes.find((x) => x.id === this.selectedId);
    if (!recipe || this.stateOf(recipe) !== 'ready') return;
    this.cb.onCraft(this.stationId, this.selectedId);
    // El server responde con inventory sync + notice; refresh() se llama desde GameScene.
  }

  public destroy(): void {
    this.clearRows();
    for (const p of this.parts) p.destroy();
    this.parts = [];
  }
}
